import { useCallback } from "react"
import type { FC, ReactElement } from "react"

import { Loadable, RootLoaderFallback } from "@/3514/components"
import {
    CoachingSessionsEntity,
    ECoachingSessionsApiMode,
    type TCoachingSessionsEntityProps
} from "@/3514/entities/coaching-sessions"
import { type TUserState, useCoachingSessionsAnimationsReset, useUser } from "@/3514/hooks"
import { type TParticipantByIdQuery, useParticipantByIdQuery } from "@/3514/queries"
import { type IUserParticipantModel } from "@/3514/types"
import type { TEmptyCallback } from "@/shared/types/functions"

import classes from "./coach.individual-participant.styles"
import { type TCoachIndividualParticipantSharedTabProps as TProps } from "./coach.individual-participant.ui.types"

const seenAnimationsPerParticipant: Map<number, boolean> = new Map()

const CoachIndividualParticipantSessionsTab: FC<TProps> = ({ participantId }: TProps): ReactElement => {
    useCoachingSessionsAnimationsReset(seenAnimationsPerParticipant, participantId)

    const { user }: TUserState = useUser()

    const { data: participant, isFetching: isParticipantFetching }: TParticipantByIdQuery = useParticipantByIdQuery({
        userId: participantId
    })

    const handleEntityMount: TEmptyCallback = useCallback(
        (): void => void seenAnimationsPerParticipant.set(participantId, true),
        [participantId]
    )

    const props: TCoachingSessionsEntityProps = {
        apiMode: ECoachingSessionsApiMode.Individual,
        user: isParticipantFetching ? ({ id: participantId } as IUserParticipantModel) : participant,
        onMount: handleEntityMount,
        sessionInfoViewerRole: user.role,
        withSwappableSessions: false,
        withCancellableSessions: true,
        withAttendanceMarkableSessions: true,
        withPostSessionModalOpeners: false,
        withGiftSessions: true,
        wrapperClassName: classes.sessionsTab.container()
    }

    return (
        <Loadable<TCoachingSessionsEntityProps>
            loadingFallback={<RootLoaderFallback top="25%" xsTop="45%" />}
            component={CoachingSessionsEntity}
            componentProps={props}
        />
    )
}

CoachIndividualParticipantSessionsTab.displayName = "CoachIndividualParticipantSessionsTab"

export default CoachIndividualParticipantSessionsTab
